import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { isRouteErrorResponse, Link } from 'react-router';
import { Button } from '@systemzero-inc/ui/primitives/button';

interface ErrorContent {
  title: string;
  description: string;
  buttonText: string;
  buttonLink: string;
  status: string;
}

interface ErrorMessageContent {
  title?: string;
  description?: string;
  buttonText?: string;
}

interface ErrorTranslations {
  title?: string;
  description?: string;
  notFound?: ErrorMessageContent;
  serverError?: ErrorMessageContent;
  validationError?: ErrorMessageContent;
}

interface FallbackUrls {
  notFound?: string;
  serverError?: string;
  validationError?: string;
  default: string;
}

interface ErrorBoundaryProps {
  className?: string;
  translations?: ErrorTranslations;
  fallbackUrls?: FallbackUrls;
  error?: unknown;
}

function getNotFoundContent(fallbackUrls: FallbackUrls, translations?: ErrorTranslations): ErrorContent {
  return {
    title: translations?.notFound?.title ?? 'Page not found',
    description: translations?.notFound?.description ?? 'The requested page could not be found.',
    buttonText: translations?.notFound?.buttonText ?? 'Go to Homepage',
    buttonLink: fallbackUrls.notFound ?? fallbackUrls.default,
    status: '404',
  };
}

function getServerErrorContent(
  fallbackUrls: FallbackUrls,
  translations?: ErrorTranslations,
  errorMessage?: string,
): ErrorContent {
  return {
    title: translations?.serverError?.title ?? 'error.serverError.title',
    description: errorMessage ?? translations?.serverError?.description ?? 'error.serverError.description',
    buttonText: translations?.serverError?.buttonText ?? 'error.serverError.retry',
    buttonLink: fallbackUrls.serverError ?? fallbackUrls.default,
    status: '500',
  };
}

function getValidationErrorContent(
  fallbackUrls: FallbackUrls,
  translations?: ErrorTranslations,
  errorMessage?: string,
): ErrorContent {
  return {
    title: translations?.validationError?.title ?? 'error.validationError.title',
    description: errorMessage ?? translations?.validationError?.description ?? 'error.validationError.description',
    buttonText: translations?.validationError?.buttonText ?? 'error.validationError.retry',
    buttonLink: fallbackUrls.validationError ?? fallbackUrls.default,
    status: '400',
  };
}

function handleAxiosError(
  error: AxiosError<{ message?: string }>,
  fallbackUrls: FallbackUrls,
  translations?: ErrorTranslations,
): ErrorContent {
  if (error.response?.status === 404) {
    return getNotFoundContent(fallbackUrls, translations);
  }
  if (error.response?.status === 400) {
    const errorMessage = error.response.data?.message ?? error.message;
    return getValidationErrorContent(fallbackUrls, translations, errorMessage);
  }
  return getServerErrorContent(fallbackUrls, translations, error.message);
}

export function ErrorBoundary({
  className,
  translations,
  fallbackUrls = { default: '/' },
  error,
}: Readonly<ErrorBoundaryProps>) {
  const { t } = useTranslation();

  // Initialize with default error content
  let errorContent: ErrorContent = {
    title: translations?.title ?? 'error.title',
    description: translations?.description ?? 'error.description',
    buttonText: 'error.goToHomepage',
    buttonLink: fallbackUrls.default,
    status: '500',
  };

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      errorContent = getNotFoundContent(fallbackUrls, translations);
    } else {
      errorContent = getServerErrorContent(fallbackUrls, translations, error.statusText);
    }
    errorContent.status = error.status.toString();
  } else if (error && typeof error === 'object' && 'error' in error) {
    const clientError = error.error as AxiosError<{ message?: string }>;
    if (clientError?.isAxiosError) {
      errorContent = handleAxiosError(clientError, fallbackUrls, translations);
    }
  } else if (error && (error as AxiosError<{ message?: string }>).isAxiosError) {
    errorContent = handleAxiosError(error as AxiosError<{ message?: string }>, fallbackUrls, translations);
  } else if (import.meta.env.DEV && error instanceof Error) {
    errorContent = getServerErrorContent(fallbackUrls, translations, error.message);
  }

  return (
    <div
      data-error-page
      className={`flex h-screen w-full flex-col items-center justify-center bg-background ${className}`}
    >
      <section className="py-16 lg:py-24" aria-labelledby="error-title">
        <div className="container px-6 flex flex-col lg:flex-row items-center gap-12 lg:gap-16 relative z-10 mx-auto">
          <div className="flex flex-col gap-6 lg:gap-8 flex-1 max-w-xl items-center text-center m-auto">
            <div className="flex flex-col gap-4 lg:gap-5">
              <p className="text-muted-foreground text-sm lg:text-base font-semibold" aria-label="Error code">
                {errorContent.status}
              </p>
              <h1 id="error-title" className="text-foreground text-3xl md:text-5xl font-bold">
                {t(errorContent.title, { defaultValue: errorContent.title })}
              </h1>
              <p className="text-muted-foreground text-base lg:text-lg">
                {t(errorContent.description, { defaultValue: errorContent.description })}
              </p>
            </div>
            <Button asChild>
              <Link to={errorContent.buttonLink}>
                {t(errorContent.buttonText, { defaultValue: errorContent.buttonText })}
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
